import { useMemo } from "react";
import SugestoesPorApp from "../files/SugestoesPorApp.json";
import { PerguntasFrequentes } from "../files/AppsPerguntas";
import { EmpreenderApps } from "../files/AppsArray";
import { Assuntos, AssuntosPadrao, AssuntosPorApp } from "../files/AssuntosPorApp";
import { useHistorico } from "../contexts/HistoricoContext";
import { useTextosInterface } from "./TextosInterface";
import { falaDeOutroApp, pareceBoaPergunta } from "../lib/filtroSugestoes";

type Idioma = "pt" | "es";
const sugestoesGeradas = SugestoesPorApp as Record<string, Partial<Record<Idioma, string[]>>>;

const unicas = (lista: string[]) => {
  const vistas = new Set<string>();
  return lista.filter((texto) => {
    const chave = texto.trim().toLowerCase();
    if (!chave || vistas.has(chave)) return false;
    vistas.add(chave);
    return true;
  });
};

// Sorteia `quantidade` itens, com mais chance para os primeiros (os mais buscados).
const sortearPonderado = (lista: string[], quantidade: number) => {
  const restantes = lista.map((texto, i) => ({ texto, peso: 1 / (i + 1) }));
  const escolhidas: string[] = [];
  while (escolhidas.length < quantidade && restantes.length) {
    const total = restantes.reduce((soma, item) => soma + item.peso, 0);
    let alvo = Math.random() * total;
    const indice = restantes.findIndex((item) => (alvo -= item.peso) <= 0);
    escolhidas.push(restantes.splice(Math.max(indice, 0), 1)[0].texto);
  }
  return escolhidas;
};

// Sugestões da tela e base do autocomplete. Fonte: perguntas reais mais buscadas por app
// (scripts/gerar-sugestoes.py), as perguntas fixas antigas e o que a própria pessoa já perguntou.
export const useSugestoes = (appSlug: string, idioma: Idioma) => {
  const { historicoApps } = useHistorico();
  const textosInterface = useTextosInterface();

  const dadosApp = EmpreenderApps.find(
    (app) => app.slug === appSlug || app.sluges === appSlug
  );
  const slugAmigavel = dadosApp?.slugamigavel || "";
  const nomeApp = (idioma === "es" ? dadosApp?.namees : dadosApp?.name) || "";

  const doApp = (lista: string[]) => lista.filter((texto) => !falaDeOutroApp(texto, slugAmigavel));
  const populares = doApp(sugestoesGeradas[slugAmigavel]?.[idioma] || []);
  const fixas = doApp((PerguntasFrequentes as Record<string, string[]>)[appSlug] || []);

  // Muda a cada visita: 3 perguntas sorteadas entre as 12 mais buscadas do app. Na tela só
  // entram frases de pergunta ("Dia das mães", "QUERO O PLANO..." ficam para o autocomplete).
  const exibidas = useMemo(() => {
    const candidatas = unicas([...populares, ...fixas]);
    const boas = candidatas.filter(pareceBoaPergunta);
    return sortearPonderado((boas.length >= 3 ? boas : candidatas).slice(0, 12), 3);
  }, [slugAmigavel, idioma]);

  const doUsuario = useMemo(() => {
    const conversas = historicoApps?.[slugAmigavel] || {};
    return Object.values(conversas)
      .flat()
      .filter((m) => m.tipo === "usuario" && !textosInterface.includes(m.texto))
      .map((m) => m.texto)
      .reverse();
  }, [historicoApps, slugAmigavel]);

  const paraAutocompletar = useMemo(() => {
    const assuntos = (AssuntosPorApp[slugAmigavel] || AssuntosPadrao).map((chave) =>
      Assuntos[chave].pergunta[idioma].replace("{{app}}", nomeApp)
    );
    return unicas([...doUsuario, ...populares, ...fixas, ...assuntos]);
  }, [doUsuario, slugAmigavel, idioma]);

  return { exibidas, paraAutocompletar };
};
